import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SendIcon from '@mui/icons-material/SendOutlined'
import CustomLoader from '../Loader/CustomLoader'
import { ConfirmDialog } from '../UI/RowActionMenu'
import {
  getReportSchedules,
  getReportSendLog,
  getRestaurantOptions,
  saveReportSchedule,
  deleteReportSchedule,
  sendStoreReportNow
} from '../../apollo'

const GET_SCHEDULES = gql`
  ${getReportSchedules}
`
const GET_SEND_LOG = gql`
  ${getReportSendLog}
`
const GET_RESTAURANTS = gql`
  ${getRestaurantOptions}
`
const SAVE_SCHEDULE = gql`
  ${saveReportSchedule}
`
const DELETE_SCHEDULE = gql`
  ${deleteReportSchedule}
`
const SEND_NOW = gql`
  ${sendStoreReportNow}
`

const CONTROL_HEIGHT = 38

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
]

// Capped at 28 so every month has the day. A schedule set to the 31st would
// simply never fire in February.
const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, index) => index + 1)

const pad = value => String(value).padStart(2, '0')

const ScheduleReports = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [deleteTarget, setDeleteTarget] = useState(null)

  const [restaurant, setRestaurant] = useState(null)
  const [frequency, setFrequency] = useState('monthly')
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [dayOfWeek, setDayOfWeek] = useState(1)
  const [time, setTime] = useState('06:00')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [feedback, setFeedback] = useState(null)

  const { data: restaurantData } = useQuery(GET_RESTAURANTS)
  const {
    data: scheduleData,
    loading: schedulesLoading,
    refetch: refetchSchedules
  } = useQuery(GET_SCHEDULES, { fetchPolicy: 'cache-and-network' })
  const { data: logData, refetch: refetchLog } = useQuery(GET_SEND_LOG, {
    variables: { limit: 25 },
    fetchPolicy: 'cache-and-network'
  })

  const [mutateSave, { loading: saving }] = useMutation(SAVE_SCHEDULE)
  const [mutateDelete] = useMutation(DELETE_SCHEDULE)
  const [mutateSendNow, { loading: sending }] = useMutation(SEND_NOW)

  const restaurants = useMemo(() => restaurantData?.restaurantOptions || [], [
    restaurantData
  ])
  const schedules = scheduleData?.reportSchedules || []
  const sendLog = logData?.reportSendLog || []

  // Clicking a row loads it into the form, so an existing schedule is edited in
  // place rather than retyped from memory.
  const loadSchedule = schedule => {
    setRestaurant(schedule.restaurant || null)
    setFrequency(schedule.frequency || 'monthly')
    setDayOfMonth(schedule.dayOfMonth || 1)
    setDayOfWeek(schedule.dayOfWeek ?? 1)
    setTime(`${pad(schedule.hour ?? 6)}:${pad(schedule.minute ?? 0)}`)
    setRecipientEmail(schedule.recipientEmail || '')
    setEnabled(schedule.enabled !== false)
    setFeedback(null)
  }

  const onSave = async () => {
    if (!restaurant) return
    const [hour, minute] = time.split(':').map(Number)
    try {
      await mutateSave({
        variables: {
          input: {
            restaurantId: restaurant._id,
            enabled,
            // Sent even when blank: an empty string is how the override is
            // cleared and the owner's email put back in charge.
            recipientEmail: recipientEmail.trim(),
            frequency,
            dayOfMonth,
            dayOfWeek,
            hour: Number.isFinite(hour) ? hour : 6,
            minute: Number.isFinite(minute) ? minute : 0
          }
        }
      })
      setFeedback({ type: 'success', message: t('ScheduleSaved') })
      refetchSchedules()
    } catch (error) {
      setFeedback({ type: 'error', message: error.message })
    }
  }

  const onDelete = async schedule => {
    try {
      await mutateDelete({
        variables: { restaurantId: schedule.restaurant?._id }
      })
      refetchSchedules()
    } catch (error) {
      setFeedback({ type: 'error', message: error.message })
    }
  }

  // Goes through the same service the scheduled job uses, so a successful test
  // proves the production path rather than a parallel one.
  const onSendNow = async schedule => {
    try {
      const result = await mutateSendNow({
        variables: { restaurantId: schedule.restaurant?._id }
      })
      const outcome = result?.data?.sendStoreReportNow
      setFeedback(
        outcome?.sent
          ? {
              type: 'success',
              message: `${t('InvoiceSentTo')} ${outcome.recipient} (${
                outcome.period
              })`
            }
          : { type: 'error', message: outcome?.reason || t('InvoiceNotSent') }
      )
      refetchLog()
      refetchSchedules()
    } catch (error) {
      setFeedback({ type: 'error', message: error.message })
    }
  }

  const describe = schedule =>
    schedule.frequency === 'weekly'
      ? `${t('Weekly')} - ${WEEKDAYS[schedule.dayOfWeek ?? 1]}`
      : `${t('Monthly')} - ${t('Day')} ${schedule.dayOfMonth}`

  const statusColor = status =>
    status === 'sent'
      ? theme.palette.success.main
      : status === 'failed'
      ? theme.palette.error.main
      : theme.palette.warning.main

  // This theme sets text.primary to #fff, so every surface and control has to
  // name its own colour or it renders white on white. Collected here rather
  // than repeated inline on a dozen elements.
  const paperSx = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    p: 3,
    mb: 3,
    borderRadius: 2,
    color: theme.palette.text.secondary
  }

  const controlRootSx = {
    height: CONTROL_HEIGHT,
    fontSize: 12,
    borderRadius: '6px',
    color: theme.palette.text.secondary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[300]
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    }
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': controlRootSx,
    '& .MuiInputBase-input': {
      fontSize: 12,
      color: theme.palette.text.secondary,
      padding: '6px 12px !important',
      height: 'auto !important'
    }
  }

  const selectSx = {
    ...controlRootSx,
    '& .MuiSelect-icon': { color: theme.palette.text.disabled }
  }

  const toggleSx = {
    height: CONTROL_HEIGHT,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 600,
      px: 2,
      color: theme.palette.text.disabled,
      borderColor: theme.palette.grey[300],
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        '&:hover': { backgroundColor: theme.palette.primary.main3 }
      }
    }
  }

  // One rule for the whole table beats colouring every cell individually.
  const tableSx = {
    '& .MuiTableCell-root': {
      fontSize: 12,
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider
    },
    '& .MuiTableCell-head': {
      fontSize: 11,
      fontWeight: 700,
      color: theme.palette.text.disabled
    }
  }

  const menuProps = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: 12,
          color: theme.palette.text.secondary
        }
      }
    }
  }

  const headingSx = {
    fontSize: 14,
    fontWeight: 700,
    mb: 2,
    color: theme.palette.text.secondary
  }

  const labelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.disabled,
    mb: 0.75
  }

  return (
    <>
      <Paper elevation={0} sx={paperSx}>
        <Typography sx={{ ...headingSx, fontSize: 16, mb: 0.5 }}>
          {t('ScheduleReports')}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: theme.palette.text.disabled, mb: 3 }}>
          {t('ScheduleReportsHelp')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2.5
          }}>
          <Box sx={{ minWidth: 240 }}>
            <Typography sx={labelSx}>{t('Store')}</Typography>
            <Autocomplete
              size="small"
              sx={{ width: '100%', ...inputSx }}
              options={restaurants}
              value={restaurant}
              onChange={(event, value) => setRestaurant(value)}
              getOptionLabel={option => option?.name || ''}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              renderOption={(props, option) => (
                <MenuItem
                  {...props}
                  key={option._id}
                  value={option._id}
                  sx={{
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}>
                  {option.name}
                </MenuItem>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={t('SelectStore')}
                  size="small"
                />
              )}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>{t('Frequency')}</Typography>
            <ToggleButtonGroup
              exclusive
              value={frequency}
              onChange={(event, value) => value && setFrequency(value)}
              sx={toggleSx}>
              <ToggleButton
                value="monthly"
                sx={{ textTransform: 'none', px: 2 }}>
                {t('Monthly')}
              </ToggleButton>
              <ToggleButton
                value="weekly"
                sx={{ textTransform: 'none', px: 2 }}>
                {t('Weekly')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Only the control that applies to the chosen frequency is shown, so
              there is never an inactive day picker to second-guess. */}
          <Box sx={{ minWidth: 150 }}>
            <Typography sx={labelSx}>
              {frequency === 'weekly' ? t('DayOfWeek') : t('DayOfMonth')}
            </Typography>
            {frequency === 'weekly' ? (
              <Select
                fullWidth
                size="small"
                value={dayOfWeek}
                onChange={event => setDayOfWeek(event.target.value)}
                MenuProps={menuProps}
                sx={selectSx}>
                {WEEKDAYS.map((day, index) => (
                  <MenuItem key={day} value={index}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                fullWidth
                size="small"
                value={dayOfMonth}
                onChange={event => setDayOfMonth(event.target.value)}
                MenuProps={menuProps}
                sx={selectSx}>
                {DAYS_OF_MONTH.map(day => (
                  <MenuItem key={day} value={day}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Box sx={{ width: 130 }}>
            <Typography sx={labelSx}>{t('Time')}</Typography>
            <TextField
              type="time"
              size="small"
              fullWidth
              sx={inputSx}
              value={time}
              onChange={event => setTime(event.target.value)}
              inputProps={{ step: 300 }}
            />
          </Box>

          <Box sx={{ minWidth: 240 }}>
            <Typography sx={labelSx}>{t('ReceiverEmail')}</Typography>
            <TextField
              type="email"
              size="small"
              fullWidth
              sx={inputSx}
              value={recipientEmail}
              onChange={event => setRecipientEmail(event.target.value)}
              placeholder={t('ReceiverEmailPlaceholder')}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>{t('Enabled')}</Typography>
            <Switch
              checked={enabled}
              onChange={event => setEnabled(event.target.checked)}
            />
          </Box>

          <Box sx={{ marginLeft: { xs: 0, lg: 'auto' } }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onSave}
              disabled={!restaurant || saving}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                height: CONTROL_HEIGHT,
                px: 3,
                borderRadius: '6px'
              }}>
              {t('SaveSchedule')}
            </Button>
          </Box>
        </Box>

        {feedback && (
          <Typography
            sx={{
              mt: 2,
              fontSize: 12,
              color:
                feedback.type === 'error'
                  ? theme.palette.error.main
                  : theme.palette.success.main
            }}>
            {feedback.message}
          </Typography>
        )}

        <Typography
          sx={{ mt: 2, fontSize: 11, color: theme.palette.text.disabled }}>
          {t('ScheduleRecipientNote')}
        </Typography>
      </Paper>

      <Paper elevation={0} sx={paperSx}>
        <Typography sx={headingSx}>{t('ActiveSchedules')}</Typography>
        {schedulesLoading && !schedules.length ? (
          <CustomLoader />
        ) : (
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>{t('Store')}</TableCell>
                <TableCell>{t('Receiver')}</TableCell>
                <TableCell>{t('Frequency')}</TableCell>
                <TableCell>{t('Time')}</TableCell>
                <TableCell>{t('LastSent')}</TableCell>
                <TableCell>{t('Status')}</TableCell>
                <TableCell align="right">{t('Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map(schedule => (
                <TableRow
                  key={schedule._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => loadSchedule(schedule)}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {schedule.restaurant?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {schedule.recipientEmail || t('OwnerEmail')}
                  </TableCell>
                  <TableCell>{describe(schedule)}</TableCell>
                  <TableCell>
                    {pad(schedule.hour ?? 6)}:{pad(schedule.minute ?? 0)}
                  </TableCell>
                  <TableCell>{schedule.lastSentPeriod || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={schedule.enabled ? t('Enabled') : t('Paused')}
                      sx={{
                        fontSize: 11,
                        color: schedule.enabled
                          ? theme.palette.success.main
                          : theme.palette.text.disabled
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      disabled={sending}
                      title={t('SendNow')}
                      onClick={event => {
                        event.stopPropagation()
                        onSendNow(schedule)
                      }}>
                      <SendIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={t('Delete')}
                      onClick={event => {
                        event.stopPropagation()
                        setDeleteTarget(schedule)
                      }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!schedules.length && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ color: theme.palette.text.disabled, fontSize: 12 }}>
                    {t('NoSchedulesYet')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper elevation={0} sx={{ ...paperSx, mb: 0 }}>
        <Typography sx={headingSx}>{t('RecentSends')}</Typography>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>{t('Store')}</TableCell>
              <TableCell>{t('Period')}</TableCell>
              <TableCell>{t('Recipient')}</TableCell>
              <TableCell>{t('Status')}</TableCell>
              <TableCell>{t('Sent')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sendLog.map(entry => (
              <TableRow key={entry._id}>
                <TableCell>{entry.restaurantName || '-'}</TableCell>
                <TableCell>{entry.period || '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {entry.recipient || '-'}
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{ fontSize: 12, color: statusColor(entry.status) }}>
                    {entry.status}
                    {entry.error ? ` - ${entry.error}` : ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {entry.createdAt
                    ? new Date(entry.createdAt).toLocaleString()
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
            {!sendLog.length && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{ color: theme.palette.text.disabled, fontSize: 12 }}>
                  {t('NoSendsYet')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`${t('Delete')}?`}
        message={t('DeleteConfirm')}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          onDelete(deleteTarget)
          setDeleteTarget(null)
        }}
      />
    </>
  )
}

export default ScheduleReports
